import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import find from 'lodash/find';
import get from 'lodash/get';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';

import baskets, * as fromBaskets from './baskets';
import basketEdits, * as fromBasketEdits from './basketEdits';
import basketFilters, * as fromBasketFilters from './basketFilters';
import basketStatus, * as fromBasketStatus from './basketStatus';
import customizeIt, * as fromCustomizeIt from './customizeIt';
import mealOptionGroups, * as fromMealOptionGroups from './mealOptionGroups';
import menuCategories, * as fromMenuCategories from './menuCategories';
import menuSlugs from './menuSlugs';
import menuStatus, * as fromMenuStatus from './menuStatus';
import menus, * as fromMenu from './menus';
import menuMeals, * as fromMenuMeals from './menuMeals';
import retailStores, * as fromRetailStores from './retailStores';
import reviews, * as fromReviews from './reviews';
import scroll, * as fromScroll from './scroll';
import status, * as fromStatus from './status';

export default combineReducers({
  baskets,
  basketEdits,
  basketFilters,
  basketStatus,
  customizeIt,
  mealOptionGroups,
  menus,
  menuMeals,
  menuCategories,
  menuSlugs,
  menuStatus,
  retailStores,
  reviews,
  scroll,
  status,
});

const selectDashboard = state => state.dashboard;

export const selectBaskets = state => selectDashboard(state).baskets;
export const selectBasketEdits = state => selectDashboard(state).basketEdits;
export const selectBasketFilters = state => selectDashboard(state).basketFilters;
export const selectCustomizeIt = state => selectDashboard(state).customizeIt;
export const selectMealOptions = state => selectDashboard(state).mealOptions;
export const selectMenus = state => selectDashboard(state).menus;
export const selectMealOptionGroups = state => selectDashboard(state).mealOptionGroups;
export const selectMenuSlugs = state => selectDashboard(state).menuSlugs;
export const selectMenuCategories = state => selectDashboard(state).menuCategories;
export const selectMenuMeals = state => selectDashboard(state).menuMeals;
export const selectScroll = state => selectDashboard(state).scroll;
export const selectStatus = state => selectDashboard(state).status;
export const selectMeal = (state, menuSlug, mealId) => {
  return (
    find(selectMealsForBasket(state, menuSlug), { id: mealId }) ||
    find(selectMealsForMenu(state, menuSlug), { id: mealId })
  );
};
export const selectRetailStores = state => selectDashboard(state).retailStores;
export const selectReviews = state => selectDashboard(state).reviews;
export const selectBasket = (state, menuSlug) =>
  fromBaskets.selectBasket(selectBaskets(state), menuSlug);
export const selectMealOptionSelectionsForBasket = (state, menuSlug) =>
  fromBaskets.selectMealOptionSelectionsForBasket(selectBaskets(state), menuSlug);
export const selectMealOptionSelectionsForBasketAndMeal = (state, menuSlug, mealId) =>
  fromBaskets.selectMealOptionSelectionsForBasketAndMeal(selectBaskets(state), menuSlug, mealId);

export const selectBasketFilter = (state, filter) =>
  fromBasketFilters.selectBasketFilter(selectBasketFilters(state), filter);
export const selectFilterLabel = (state, filter) =>
  fromBasketFilters.selectFilterLabel(selectBasketFilters(state), filter);
export const selectTotalFiltersApplied = state =>
  fromBasketFilters.selectTotalFiltersApplied(selectBasketFilters(state));

export const selectMenu = (state, menuSlug) => fromMenu.selectMenu(selectMenus(state), menuSlug);
export const selectMealAddons = createSelector(
  selectMenuMeals,
  menuMeals => fromMenuMeals.selectMealAddons(menuMeals)
);
export const selectOptIns = createSelector(
  selectMenuMeals,
  menuMeals => fromMenuMeals.selectOptIns(menuMeals)
);
export const selectMealsForMenu = (state, menuSlug) =>
  fromMenuMeals.selectMealsForMenu(selectMenuMeals(state), menuSlug);
export const selectNonDonationMealsForMenu = (state, menuSlug) =>
  fromMenuMeals.selectNonDonationMealsForMenu(selectMenuMeals(state), menuSlug);
export const selectMealsForBasket = (state, menuSlug) =>
  fromBaskets.selectMealsForBasket(selectBaskets(state), menuSlug);

const selectAvailableMealOptionGroups = createSelector(
  selectMenuSlugs,
  selectMealOptionGroups,
  selectBaskets,
  (menuSlugs, mealOptionGroups, baskets) => {
    const isOptionSelected = (menuSlug, mealId, optionId) => {
      const optionSelectionsForMeal = fromBaskets.selectMealOptionSelectionsForBasketAndMeal(
        baskets,
        menuSlug,
        mealId
      );

      return optionSelectionsForMeal.some(selection => selection.mealOptionId === optionId);
    };

    const isOptionAvailable = (menuSlug, mealId, option) => {
      if (!option.soldOut) return true;
      return isOptionSelected(menuSlug, mealId, option.id);
    };

    return menuSlugs.reduce((availableGroupsByMenu, menuSlug) => {
      const allGroupsForMenu = fromMealOptionGroups.selectMealOptionGroupsForMenu(
        mealOptionGroups,
        menuSlug
      );

      const availableGroupsForMenu = allGroupsForMenu
        .map(group => {
          const availableOptions = group.mealOptions.filter(option =>
            isOptionAvailable(menuSlug, group.mealId, option)
          );

          return {
            ...group,
            mealOptions: availableOptions,
          };
        })
        .filter(group => group.mealOptions.length > 1);

      return {
        ...availableGroupsByMenu,
        [menuSlug]: availableGroupsForMenu,
      };
    }, {});
  }
);

export const selectMealOptionGroupsForMenu = (state, menuSlug) =>
  fromMealOptionGroups.selectMealOptionGroupsForMenu(
    selectAvailableMealOptionGroups(state),
    menuSlug
  );

export const selectMealOptionGroupsForMenuAndMeal = (state, menuSlug, mealId) =>
  fromMealOptionGroups.selectMealOptionGroupsForMenuAndMeal(
    selectAvailableMealOptionGroups(state),
    menuSlug,
    mealId
  );

export const selectMealOptionGroup = (state, menuSlug, mealId, mealOptionGroupId) =>
  fromMealOptionGroups.selectMealOptionGroup(
    selectMealOptionGroups(state),
    menuSlug,
    mealId,
    mealOptionGroupId
  );

const selectBasketStatus = state => selectDashboard(state).basketStatus;
const selectMenuStatus = state => selectDashboard(state).menuStatus;

export const basketNeedsInitialization = (state, menuSlug) =>
  fromBasketStatus.needsInitialization(selectBasketStatus(state), menuSlug);
export const isBasketLoading = (state, menuSlug) =>
  fromBasketStatus.isBasketLoading(selectBasketStatus(state), menuSlug);
export const isBasketError = (state, menuSlug) =>
  fromBasketStatus.isBasketError(selectBasketStatus(state), menuSlug);
export const removedMeals = (state, menuSlug) =>
  fromBasketStatus.removedMeals(selectBasketStatus(state), menuSlug);

export const isInitializationComplete = state =>
  fromStatus.isInitializationComplete(selectStatus(state));
export const needsInitializing = state => fromStatus.needsInitializing(selectStatus(state));
export const isUnauthorized = state => fromStatus.isUnauthorized(selectStatus(state));

export const menuNeedsInitialization = (state, menuSlug) =>
  fromMenuStatus.needsInitialization(selectMenuStatus(state), menuSlug);
export const isMenuLoading = (state, menuSlug) =>
  fromMenuStatus.isMenuLoading(selectMenuStatus(state), menuSlug);
export const isMenuError = (state, menuSlug) =>
  fromMenuStatus.isMenuError(selectMenuStatus(state), menuSlug);

export const selectScrollPositionForRoute = (state, route) =>
  fromScroll.selectScrollPositionForRoute(selectScroll(state), route);

export const selectEditableMenuSlugs = createSelector(
  selectMenuSlugs,
  selectBaskets,
  (menuSlugs, baskets) => menuSlugs.filter(slug => get(baskets, [slug, 'futureBasket'], false))
);

export const selectInitialMenuSlugs = createSelector(
  selectMenuSlugs,
  menuSlugs => menuSlugs.slice(0, 2)
);

const sortAsc = (x, y) => x - y;

export const selectOrderedBaskets = createSelector(
  selectBaskets,
  baskets => {
    return Object.values(baskets).sort((basketA, basketB) => {
      return sortAsc(new Date(basketA.menu.endsAt), new Date(basketB.menu.endsAt));
    });
  }
);

export const selectNextOrderableBasket = createSelector(
  selectOrderedBaskets,
  orderedBaskets => {
    return find(orderedBaskets, {
      showDiscounts: true,
      scheduled: true,
      futureBasket: true,
    });
  }
);

export const selectFilteredMeals = createSelector(
  selectNonDonationMealsForMenu,
  selectMealsForBasket,
  selectBasketFilters,
  (meals, selectedMeals, filters) => {
    let [matches, notMatches] = partition(meals, meal =>
      fromBasketFilters.checkMealFilters(meal, filters)
    );

    const selectedMealIds = selectedMeals.map(selectedMeal => selectedMeal.id);

    const sortMealsbySelected = meal => {
      return !selectedMealIds.includes(meal.id);
    };

    matches = sortBy(matches, sortMealsbySelected);
    notMatches = sortBy(notMatches, sortMealsbySelected);
    return { matches, notMatches };
  }
);

export const selectCustomizeItForMealId = (state, mealId) =>
  fromCustomizeIt.selectCustomizeItForMealId(selectCustomizeIt(state), mealId);

export const selectMealServingsForBasketEdits = state =>
  fromBasketEdits.selectServings(selectBasketEdits(state));
export const selectMealOptionsForBasketEdits = state =>
  fromBasketEdits.selectMealOptions(selectBasketEdits(state));
export const selectMealOptionsForBasketAndMeal = (state, mealId) =>
  fromBasketEdits.selectMealOptions(state, mealId);
export const selectSubtotalsForBasketEdits = state =>
  fromBasketEdits.selectSubtotals(selectBasketEdits(state));

export const selectStoreForMenu = (state, menuSlug) =>
  fromRetailStores.selectStoreForMenu(selectRetailStores(state), menuSlug);
export const selectReview = (state, mealId) =>
  fromReviews.selectReview(selectReviews(state), mealId);
export const selectComments = state => fromReviews.selectComments(selectReviews(state));

export const selectMenuCategoriesLoading = state =>
  fromMenuCategories.selectMenuCategoriesLoading(selectMenuCategories(state));
export const selectMenuCategoriesData = state =>
  fromMenuCategories.selectMenuCategoriesData(selectMenuCategories(state));
